import React, { useRef, useState } from "react";
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useSpring } from "framer-motion";
import { Section, Reveal } from "@/components/network/NetworkAtoms";

const BEATS = [
  { when: "Within the hour", title: "Your case reaches a person", body: "Not a queue and not an auto-responder. It is read, and anything a funder will inevitably ask for is flagged before it costs you a day." },
  { when: "Same working day", title: "You get our read on it", body: "A short, direct note: where this is fundable, which structures make sense, and what in the file needs addressing first." },
  { when: "Same day, usually", title: "It goes to matched funders", body: "Only to the funders whose criteria the case meets, and only with what they need to form a view." },
  { when: "24–48 hours", title: "Terms start coming back", body: "Some funders respond in hours, others in a day or two. You hear the outcome either way, including the declines and the reason." },
  { when: "On receipt of terms", title: "We translate them", body: "Rate, term, total cost, security, covenants, early repayment. Side by side, in plain figures, before anything is signed." },
  { when: "After drawdown", title: "The same contact stays with it", body: "The person who placed the facility remains your point of contact — for the next facility, a review, or a change in the business." },
];

export default function First48() {
  const reduce = useReducedMotion();
  const track = useRef(null);
  const [active, setActive] = useState(0);

  // The line fills, and the blue dot rides down it, as you read.
  const { scrollYProgress } = useScroll({ target: track, offset: ["start 65%", "end 65%"] });
  const fill = useSpring(scrollYProgress, { stiffness: 120, damping: 24, restDelta: 0.001 });
  useMotionValueEvent(scrollYProgress, "change", (p) => {
    setActive(Math.min(BEATS.length - 1, Math.max(0, Math.round(p * (BEATS.length - 1)))));
  });

  return (
    <Section
      testId="funding-first-48"
      eyebrow="What happens next"
      title="The first 48 hours, hour by hour"
      lede="Silence is the worst part of raising finance. This is the timetable we hold ourselves to, so you always
            know what should be happening and when."
    >
      <div className="mt-12 grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7">
              <div className="font-cabinet text-[42px] leading-none font-bold text-white tracking-[-0.04em]">48<span className="text-[#7da9ff]">h</span></div>
              <p className="mt-4 text-[14.5px] leading-relaxed text-slate-300">
                Most straightforward cases have terms on the table inside two working days. Property, development
                and larger structured transactions run to valuations, monitoring surveyors and credit committees —
                we give you that timetable on day one rather than at the end of week two.
              </p>
              <p className="mt-4 text-[13px] leading-relaxed text-slate-500">
                Every case carries a deadline in the system. If a funder or a document slips, it escalates
                internally before you notice.
              </p>
            </div>
          </Reveal>
        </div>

        <ol ref={track} className="relative space-y-4" data-testid="first48-track">
          <span aria-hidden className="absolute left-[7px] top-2 bottom-2 w-px bg-white/10" />
          <motion.span aria-hidden data-testid="first48-progress"
                       className="absolute left-[7px] top-2 bottom-2 w-px origin-top bg-gradient-to-b from-[#0055FF] to-[#7da9ff]"
                       style={reduce ? { scaleY: 1 } : { scaleY: fill }} />
          {BEATS.map((b, i) => {
            const done = i <= active;
            const now = i === active;
            return (
              <Reveal key={b.title} delay={i * 0.06}>
                <li className="relative pl-9" data-testid={`first48-${i}`}>
                  <span aria-hidden
                        className={`absolute left-0 top-1.5 size-[15px] rounded-full border-2 transition-colors duration-500 ${
                          done ? "border-[#0055FF] bg-[#0055FF]" : "border-white/25 bg-[#0B1220]"}`} />
                  {now && !reduce && (
                    <motion.span aria-hidden className="absolute left-0 top-1.5 size-[15px] rounded-full border border-[#0055FF]"
                                 animate={{ opacity: [0.8, 0, 0.8], scale: [1, 1.9, 1] }}
                                 transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }} />
                  )}
                  <div className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-[#7da9ff]">{b.when}</div>
                  <h3 className="mt-2 text-[17px] font-semibold text-white tracking-tight">{b.title}</h3>
                  <p className="mt-2 text-[14.5px] leading-relaxed text-slate-300 max-w-xl">{b.body}</p>
                </li>
              </Reveal>
            );
          })}
        </ol>
      </div>
    </Section>
  );
}
